/**
 * <EarningsCalendar> — 이번 주/다음 주 실적 발표 예정
 *
 * 데이터: GET /api/ticker/calendar/earnings (Alpha Vantage 3month 전체)
 * 프론트에서 오늘~N일 필터 + 날짜별 그룹
 *
 * options.days: 기본 7
 */
"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import type { WidgetMeta, WidgetProps } from "../types";
import { koreanCompanyName } from "@/lib/korean-company-name";

interface EarningsItem {
  symbol: string;
  name: string;
  reportDate: string;       // "YYYY-MM-DD"
  fiscalDateEnding: string;
  estimate: number | null;
  currency: string;
}

interface EarningsData {
  updatedAt: string;
  items: EarningsItem[];
}

const REFRESH_MS = 60 * 60 * 1000;

function formatDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const mm = d.getMonth() + 1;
  const dd = d.getDate();
  const dow = ["일", "월", "화", "수", "목", "금", "토"][d.getDay()];
  return `${mm}/${dd} (${dow})`;
}

export function EarningsCalendar({ options }: WidgetProps) {
  const days = typeof options?.days === "number" ? options.days : 7;
  const [data, setData] = useState<EarningsData | null>(null);
  const [loading, setLoading] = useState(true);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    async function load() {
      try {
        // localStorage 의 사용자 관심종목 watchlist 읽어서 함께 전달
        // 백엔드는 이 리스트 + 한국 retail 인기 top 50 만 필터링
        let watchlist = "";
        try {
          const raw = window.localStorage.getItem("dashboard:watchlist");
          if (raw) {
            const arr = JSON.parse(raw);
            if (Array.isArray(arr)) watchlist = arr.map(String).join(",");
          }
        } catch { /* ignore */ }
        const qs = `?filter=user-relevant${watchlist ? `&watchlist=${encodeURIComponent(watchlist)}` : ""}`;
        const res = await fetch(`/api/ticker/calendar/earnings${qs}`, { credentials: "include" });
        if (!res.ok) throw new Error();
        const d = (await res.json()) as EarningsData;
        if (mountedRef.current) {
          setData(d);
          setLoading(false);
        }
      } catch {
        if (mountedRef.current) setLoading(false);
      }
    }
    load();
    const t = setInterval(load, REFRESH_MS);
    return () => {
      mountedRef.current = false;
      clearInterval(t);
    };
  }, []);

  const groups = useMemo(() => {
    if (!data?.items) return [];
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    const end = new Date(now);
    end.setDate(end.getDate() + days);

    const filtered = data.items
      .filter((x) => {
        const d = new Date(x.reportDate);
        return !Number.isNaN(d.getTime()) && d >= now && d <= end;
      })
      .sort((a, b) => a.reportDate.localeCompare(b.reportDate));

    const map = new Map<string, EarningsItem[]>();
    for (const it of filtered) {
      const list = map.get(it.reportDate) ?? [];
      list.push(it);
      map.set(it.reportDate, list);
    }
    return Array.from(map.entries()).map(([date, items]) => ({ date, items }));
  }, [data, days]);

  return (
    <div className="dw-body dw-cal">
      <div className="dw-head">
        <span className="dw-title">실적 캘린더</span>
        <span className="dw-cal-range">{days}일 내</span>
      </div>
      <div className="dw-cal-body">
        {loading && !data ? (
          <div className="dw-cal-skel">
            <span className="bs-skel" style={{ width: "60%", height: 12, display: "block", marginBottom: 8 }} />
            <span className="bs-skel" style={{ width: "40%", height: 11, display: "block" }} />
          </div>
        ) : groups.length === 0 ? (
          <div className="dw-cal-empty">{days}일 내 실적 발표 예정 없음</div>
        ) : (
          groups.map((g) => (
            <div key={g.date} className="dw-cal-group">
              <div className="dw-cal-date">{formatDate(g.date)}</div>
              <div className="dw-cal-tickers">
                {g.items.slice(0, 10).map((it) => (
                  <span key={it.symbol} className="dw-cal-ticker" title={`${it.symbol} · ${koreanCompanyName(it.symbol, it.name)}`}>
                    {koreanCompanyName(it.symbol, it.name)}
                  </span>
                ))}
                {g.items.length > 10 && (
                  <span className="dw-cal-more">+{g.items.length - 10}</span>
                )}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

export const earningsCalendarMeta: WidgetMeta = {
  type: "earnings-calendar",
  name: "실적 캘린더",
  description: "이번 주/다음 주 실적 발표 예정",
  icon: "📅",
  category: "info",
  defaultSize: { w: 6, h: 4 },
  availableSizes: [
    { w: 4, h: 4 },
    { w: 6, h: 4 },
    { w: 6, h: 6 },
  ],
  minW: 3,
  maxW: 12,
  minH: 3,
  maxH: 10,
  component: EarningsCalendar,
};
